import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [adminUid, setAdminUid] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');

  const handleGrantAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminMessage('');
    setAddingAdmin(true);

    try {
      if (!adminUid.trim()) {
        setAdminMessage('Please enter a user UID');
        setAddingAdmin(false);
        return;
      }

      // Create admin document
      await setDoc(doc(db, 'admins', adminUid.trim()), {
        role: 'admin',
        grantedBy: user?.email || user?.uid,
        grantedAt: new Date().toISOString()
      });

      setAdminMessage(`✅ Admin access granted! User ${adminUid} can now log in.`);
      setAdminUid('');
    } catch (error: any) {
      console.error('Error granting admin access:', error);
      setAdminMessage(`❌ Error: ${error.message}`);
    } finally {
      setAddingAdmin(false);
    }
  };

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <h3>Admin Panel</h3>
        <nav>
          <ul>
            <li><Link to="/admin">Dashboard</Link></li>
            <li><Link to="/admin/content">Content</Link></li>
            <li><Link to="/admin/board">Board Members</Link></li>
            <li><Link to="/admin/programs">Programs</Link></li>
            <li><Link to="/admin/events">Events</Link></li>
            <li><Link to="/admin/blog">Blog Posts</Link></li>
            <li><Link to="/admin/volunteers">Volunteer Applications</Link></li>
            <li><Link to="/admin/contact">Contact Submissions</Link></li>
            <li><Link to="/admin/donation">Donation Settings</Link></li>
            <li><button onClick={logout}>Logout</button></li>
          </ul>
        </nav>
      </div>
      <div className="admin-content">
        <h1>Dashboard</h1>
        <p>Welcome, {user?.email}!</p>
        
        <div className="dashboard-grid">
          <div className="card">
            <h3>Content Management</h3>
            <p>Update website content, mission, and vision statements.</p>
            <Link to="/admin/content" className="btn btn-primary">Manage Content</Link>
          </div>
          
          <div className="card">
            <h3>Board Members</h3>
            <p>Add, edit, or remove board member profiles and photos.</p>
            <Link to="/admin/board" className="btn btn-primary">Manage Board</Link>
          </div>
          
          <div className="card">
            <h3>Programs</h3>
            <p>Create and manage foundation programs and descriptions.</p>
            <Link to="/admin/programs" className="btn btn-primary">Manage Programs</Link>
          </div>
          
          <div className="card">
            <h3>Events</h3>
            <p>Schedule and manage upcoming events and activities.</p>
            <Link to="/admin/events" className="btn btn-primary">Manage Events</Link>
          </div>
          
          <div className="card">
            <h3>Blog Posts</h3>
            <p>Create, edit, and manage blog posts for the website.</p>
            <Link to="/admin/blog" className="btn btn-primary">Manage Blog</Link>
          </div>
          
          <div className="card">
            <h3>Volunteer Applications</h3>
            <p>View and manage volunteer applications submitted through the website.</p>
            <Link to="/admin/volunteers" className="btn btn-primary">View Applications</Link>
          </div>
          
          <div className="card">
            <h3>Contact Submissions</h3>
            <p>View messages submitted through the Contact Us form.</p>
            <Link to="/admin/contact" className="btn btn-primary">View Submissions</Link>
          </div>
          
          <div className="card">
            <h3>Donation Settings</h3>
            <p>Configure donation options and payment methods.</p>
            <Link to="/admin/donation" className="btn btn-primary">Manage Donations</Link>
          </div>
        </div>

        {/* Admin Management Section */}
        <div className="card" style={{ marginTop: '2rem', maxWidth: '600px' }}>
          <h3>Grant Admin Access</h3>
          <p>To grant admin access to a user, you need their Firebase User UID.</p>
          <p style={{ fontSize: '0.9em', color: '#666', marginTop: '0.5rem' }}>
            <strong>How to find a user's UID:</strong><br />
            1. Have them try to log in (even if it fails)<br />
            2. Check the browser console - it will show their UID in the admin check log<br />
            3. Or find it in Firebase Console → Authentication → Users
          </p>
          
          <form onSubmit={handleGrantAdmin} style={{ marginTop: '1rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="adminUid" style={{ display: 'block', marginBottom: '0.5rem' }}>
                User UID:
              </label>
              <input
                type="text"
                id="adminUid"
                value={adminUid}
                onChange={(e) => setAdminUid(e.target.value)}
                placeholder="Enter Firebase User UID"
                required
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.9em',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={addingAdmin}
              className="btn btn-primary"
            >
              {addingAdmin ? 'Granting...' : 'Grant Admin Access'}
            </button>
          </form>
          
          {adminMessage && (
            <div
              style={{
                marginTop: '1rem',
                padding: '0.75rem',
                borderRadius: '4px',
                backgroundColor: adminMessage.includes('✅') ? '#d4edda' : '#f8d7da',
                color: adminMessage.includes('✅') ? '#155724' : '#721c24',
                fontSize: '0.9em'
              }}
            >
              {adminMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
